import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { BadgeBanner } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import { Button, Text } from '@frontend/design-system';

const BANNER_STORAGE_KEY = 'schedule-banner-availability';
const DISMISS_EVENT_NAME = 'schedule-settings:availability-rules:availability-banner:dismiss-button';

const getStorageKey = (baseKey: string, userId: string, locationIds: string[]) => {
  return `${baseKey}-${userId}-${[...locationIds].sort().join('-')}`;
};

export const NewAvailabilityRulesExperienceBadgeBanner = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('schedule');
  const [isDismissed, setIsDismissed] = useState(true); // default to dismissed until we check localStorage
  const { selectedLocationIds } = useAppScopeStore();
  const userId = getUser()?.userID || 'unknown-user';
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  const handleDismiss = () => {
    const storageKey = getStorageKey(BANNER_STORAGE_KEY, userId, selectedLocationIds);
    localStorage.setItem(storageKey, 'true');
    setIsDismissed(true);

    pendo.track(DISMISS_EVENT_NAME, {
      locationId,
      userId,
    });
  };

  useEffect(() => {
    const storageKey = getStorageKey(BANNER_STORAGE_KEY, userId, selectedLocationIds);
    const stored = localStorage.getItem(storageKey);
    setIsDismissed(stored === 'true');
  }, [userId, selectedLocationIds]);

  if (isDismissed) {
    return null;
  }

  return (
    <BadgeBanner styles={{ width: '100%' }}>
      <div css={contentStyles}>
        <BadgeBanner.AnimatedBadge label={t('New')} />
        <div css={{ flex: 1 }}>
          {!isV3OperatorySupported && (
            <Text size='medium' weight='bold'>
              {t('New look, same data.')}
            </Text>
          )}
          <Text size='medium' css={!isV3OperatorySupported ? { maxWidth: 700 } : undefined}>
            {isV3OperatorySupported
              ? t(
                  'You now have access to Operatory Availability! To use it, switch “Availability Calculated By” to Operatory, then review your availability to make sure everything looks right.'
                )
              : t(
                  'We’ve updated the scheduling availability experience with new functionality, but your existing data has been preserved, so there’s nothing you need to change.'
                )}
          </Text>
        </div>
        <Button iconName='x-small' variant='secondary' onClick={handleDismiss} label={t('Dismiss')} />
      </div>
    </BadgeBanner>
  );
};

const contentStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--fabric-space-gap-md);
`;
